import { useMemo, useState, useEffect } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/hooks/useCurrency';

import { assumptionsService } from '@/services/demandPlanning/assumptions.service';

import { useScopeFilterOptions, type ScopeFilterOptions } from '../hooks/useScopeFilterOptions';
import { EntityScopeFilterBar } from './EntityScopeFilterBar';
import { SopConfirmDialog } from './SopConfirmDialog';
import type {
  AssumptionsImpactSummary,
  ConsensusAssumptionRow,
  CreateAssumptionPayload,
  ManualAssumptionImpactInput,
  PlanScopeFamily,
  AssumptionDetail,
  UpdateAssumptionPayload,
} from '../types/consensus.types';
import { resolveProductCodesToIds, fetchProductCodeById } from '../utils/entityScopeProductOptions';
import { sopEntityScopeFilterBarOptions } from '../utils/sopScopeFilterBarOptions';
import { type EntityScopeFilters } from './ConsensusScopeFilters';

const ASSUMPTION_TYPES = [
  'opportunity',
  'risk',
  'promotion',
  'market_event',
  'customer_change',
  'supply_constraint',
  'capacity_constraint',
  'portfolio_change',
] as const;

function emptyImpactRow(startBucket = ''): ManualAssumptionImpactInput {
  return {
    family_codes: [],
    subfamily_codes: [],
    product_codes: [],
    warehouse_codes: [],
    supplier_codes: [],
    time_bucket: startBucket,
    impact_type: 'percentage',
    impact_percentage: '10',
    impact_qty: '',
    notes: '',
  };
}

function defaultForm() {
  return {
    title: '',
    description: '',
    assumption_types: ['opportunity'] as string[],
    start_bucket: '',
    end_bucket: '',
    severity: 'medium',
    probabilityPercent: '50',
    impact_mode: 'manual' as 'auto' | 'manual',
  };
}

import { resolveUiLocale, formatUiDate, formatUiNumber } from '@/utils/uiLocale';

function formatVariation(pct?: number | null): string {
  if (pct == null) return '—';
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

function variationClass(value?: number | null): string {
  if (value == null || value === 0) return 'text-gray-900';
  return value > 0 ? 'text-emerald-600' : 'text-red-600';
}

function formatProbability(probability?: number | null): string {
  if (probability == null) return '—';
  return `${Math.round(probability * 100)}%`;
}

const SEVERITY_STYLES: Record<string, string> = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

function impactScopeKind(imp: AssumptionDetail['impacts'][number]): 'plan_wide' | 'product' | 'subfamily' | 'family' {
  if (imp.product != null) return 'product';
  if (imp.subfamily != null) return 'subfamily';
  if (imp.family != null) return 'family';
  return 'plan_wide';
}

function impactGroupKey(imp: AssumptionDetail['impacts'][number]): string {
  return [
    impactScopeKind(imp),
    imp.time_bucket?.slice(0, 10) ?? '',
    imp.impact_type,
    String(imp.impact_percentage ?? ''),
    String(imp.impact_qty ?? ''),
    imp.notes ?? '',
  ].join('|');
}

async function buildImpactRowsFromDetail(
  data: AssumptionDetail,
  scope: ScopeFilterOptions,
): Promise<ManualAssumptionImpactInput[]> {
  if (!data.impacts.length) {
    return [emptyImpactRow(data.start_bucket.slice(0, 10))];
  }

  const groups = new Map<string, AssumptionDetail['impacts']>();
  for (const imp of data.impacts) {
    const key = impactGroupKey(imp);
    const bucket = groups.get(key);
    if (bucket) bucket.push(imp);
    else groups.set(key, [imp]);
  }

  const productIds = new Set<number>();
  for (const imp of data.impacts) {
    if (imp.product != null) productIds.add(imp.product);
  }
  const productCodeById = new Map<number, string>();
  await Promise.all(
    [...productIds].map(async (id) => {
      const code = await fetchProductCodeById(id);
      if (code) productCodeById.set(id, code);
    }),
  );

  return [...groups.values()].map((impacts) => {
    const first = impacts[0];
    const familySet = new Set<string>();
    const subfamilySet = new Set<string>();
    const productSet = new Set<string>();
    const supplierSet = new Set<string>();

    for (const imp of impacts) {
      if (imp.family != null) {
        const code = scope.familyIdToCode.get(imp.family);
        if (code) familySet.add(code);
      }
      if (imp.subfamily != null) {
        const code = scope.subfamilyIdToCode.get(imp.subfamily);
        if (code) subfamilySet.add(code);
      }
      if (imp.product != null) {
        const code = productCodeById.get(imp.product);
        if (code) productSet.add(code);
      }
      if (imp.supplier != null) {
        const code = scope.supplierIdToCode.get(imp.supplier);
        if (code) supplierSet.add(code);
      }
    }

    const scopeKind = impactScopeKind(first);
    const allPlanWide =
      scopeKind === 'plan_wide' &&
      familySet.size === 0 &&
      subfamilySet.size === 0 &&
      productSet.size === 0 &&
      supplierSet.size === 0;

    return {
      all_plan_families: allPlanWide,
      family_codes: [...familySet],
      subfamily_codes: [...subfamilySet],
      product_codes: [...productSet],
      warehouse_codes: [],
      supplier_codes: [...supplierSet],
      time_bucket: first.time_bucket?.slice(0, 10) ?? data.start_bucket.slice(0, 10),
      impact_type: first.impact_type === 'absolute_qty' ? 'absolute_qty' : 'percentage',
      impact_percentage:
        first.impact_percentage != null ? String(first.impact_percentage) : '10',
      impact_qty: first.impact_qty != null ? String(first.impact_qty) : '',
      notes: first.notes ?? '',
    };
  });
}

interface Props {
  rows: ConsensusAssumptionRow[];
  cycleTitle: string;
  impactSummary?: AssumptionsImpactSummary;
  planFamilies?: PlanScopeFamily[];
  scopeOptions?: ScopeFilterOptions;
  loading?: boolean;
  saving?: boolean;
  deleting?: boolean;
  onCreate: (payload: CreateAssumptionPayload) => Promise<void>;
  onUpdate: (id: number, payload: UpdateAssumptionPayload) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

export function AssumptionsPanel({
  rows,
  cycleTitle,
  impactSummary,
  planFamilies,
  scopeOptions: scopeOptionsProp,
  loading,
  saving,
  deleting,
  onCreate,
  onUpdate,
  onDelete,
}: Props) {
  const { t, i18n } = useTranslation();
  const uiLocale = resolveUiLocale(i18n.language);
  const formatDate = (value: string) =>
    formatUiDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' }, uiLocale);
  const formatNumber = (value?: number | null) =>
    value == null ? '—' : formatUiNumber(Math.round(value), { maximumFractionDigits: 0 }, uiLocale);
  const formatSignedNumber = (value?: number | null) => {
    if (value == null) return '—';
    const sign = value > 0 ? '+' : '';
    return `${sign}${formatUiNumber(Math.round(value), { maximumFractionDigits: 0 }, uiLocale)}`;
  };
  const { formatRounded, symbol } = useCurrency();
  const formatSignedEuro = (value?: number | null) => {
    if (value == null) return '—';
    const sign = value > 0 ? '+' : '';
    return `${sign}${formatRounded(value)}`;
  };
  const scopeFromHook = useScopeFilterOptions(scopeOptionsProp == null, planFamilies);
  const scope = scopeOptionsProp ?? scopeFromHook;
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [editDetail, setEditDetail] = useState<AssumptionDetail | null>(null);
  const [form, setForm] = useState(defaultForm);
  const [impactRows, setImpactRows] = useState<ManualAssumptionImpactInput[]>([emptyImpactRow()]);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConsensusAssumptionRow | null>(null);

  const inputClass =
    'mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-stocker-primary focus:ring-1 focus:ring-stocker-primary';

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? '')),
    [rows],
  );

  const expandImpactRow = async (
    row: ManualAssumptionImpactInput,
    defaultTimeBucket: string,
  ): Promise<CreateAssumptionPayload['impacts_input']> => {
    const time_bucket = row.time_bucket || defaultTimeBucket;
    const impact_percentage =
      row.impact_type === 'percentage' ? Number(row.impact_percentage) : null;
    const impact_qty = row.impact_type === 'absolute_qty' ? Number(row.impact_qty) : null;

    const warehouseIds = row.warehouse_codes.length
      ? row.warehouse_codes
          .map((code) => scope.warehouseCodeToId.get(code))
          .filter((id): id is number => id != null)
      : [null];
    const supplierIds = row.supplier_codes.length
      ? row.supplier_codes
          .map((code) => scope.supplierCodeToId.get(code))
          .filter((id): id is number => id != null)
      : [null];

    const productCodeToId = await resolveProductCodesToIds(row.product_codes);
    const productIds = row.product_codes
      .map((code) => productCodeToId.get(code))
      .filter((id): id is number => id != null);
    const subfamilyIds = row.subfamily_codes
      .map((code) => scope.subfamilyCodeToId.get(code))
      .filter((id): id is number => id != null);
    const familyIds = row.family_codes
      .map((code) => scope.familyCodeToId.get(code))
      .filter((id): id is number => id != null);

    const hasResolvedWarehouse = row.warehouse_codes.length > 0 && warehouseIds.some((id) => id != null);
    const hasResolvedSupplier = row.supplier_codes.length > 0 && supplierIds.some((id) => id != null);

    // Pick the most specific product-hierarchy dimension. When none is set we
    // still emit a single scope-less entity so warehouse/supplier-only impacts
    // (or plan-wide) are valid: the backend scopes them to that supplier/warehouse.
    let entities: Array<Pick<CreateAssumptionPayload['impacts_input'][number], 'family' | 'subfamily' | 'product'>>;
    if (row.all_plan_families) {
      entities = [{}];
    } else if (productIds.length) {
      entities = productIds.map((product) => ({ product }));
    } else if (subfamilyIds.length) {
      entities = subfamilyIds.map((subfamily) => ({ subfamily }));
    } else if (familyIds.length) {
      entities = familyIds.map((family) => ({ family }));
    } else if (hasResolvedWarehouse || hasResolvedSupplier) {
      entities = [{}];
    } else {
      return [];
    }

    const expanded: CreateAssumptionPayload['impacts_input'] = [];
    for (const entity of entities) {
      for (const warehouse of warehouseIds) {
        for (const supplier of supplierIds) {
          expanded.push({
            ...entity,
            warehouse,
            supplier,
            time_bucket,
            impact_type: row.impact_type,
            impact_percentage,
            impact_qty,
            notes: row.notes,
          });
        }
      }
    }
    return expanded;
  };

  const buildPayload = async (): Promise<CreateAssumptionPayload | null> => {
    if (!form.title.trim() || !form.start_bucket || !form.end_bucket || !form.assumption_types.length) {
      return null;
    }
    const probability = Math.min(100, Math.max(0, Number(form.probabilityPercent) || 0)) / 100;
    const impacts_input =
      form.impact_mode === 'manual'
        ? (
            await Promise.all(impactRows.map((row) => expandImpactRow(row, form.start_bucket)))
          ).flat()
        : [];
    if (form.impact_mode === 'manual' && impacts_input.length === 0) return null;

    return {
      title: form.title.trim(),
      description: form.description,
      assumption_types: form.assumption_types,
      assumption_type: form.assumption_types[0],
      start_bucket: form.start_bucket,
      end_bucket: form.end_bucket,
      severity: form.severity,
      probability,
      impact_mode: form.impact_mode,
      impacts_input,
    };
  };

  const resetForm = () => {
    setEditingId(null);
    setEditDetail(null);
    setForm(defaultForm());
    setImpactRows([emptyImpactRow()]);
    setError(null);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const startEdit = async (row: ConsensusAssumptionRow) => {
    setEditingId(row.id);
    setShowForm(true);
    setLoadingEdit(true);
    try {
      const data = await assumptionsService.getById(row.id);
      setForm({
        title: data.title,
        description: data.description,
        assumption_types: data.assumption_types?.length ? data.assumption_types : [data.assumption_type],
        start_bucket: data.start_bucket.slice(0, 10),
        end_bucket: data.end_bucket.slice(0, 10),
        severity: data.severity,
        probabilityPercent: String(Math.round((data.probability ?? 0) * 100)),
        impact_mode: 'manual',
      });
      setEditDetail(data);
    } catch {
      setLoadingEdit(false);
    }
  };

  useEffect(() => {
    if (editDetail == null || scope.isLoading) return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = await buildImpactRowsFromDetail(editDetail, scope);
        if (!cancelled) setImpactRows(rows);
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    editDetail,
    scope.isLoading,
    scope.familyIdToCode,
    scope.subfamilyIdToCode,
    scope.supplierIdToCode,
  ]);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await onDelete(deleteTarget.id);
    if (editingId === deleteTarget.id) closeForm();
    setDeleteTarget(null);
  };

  const submit = async () => {
    setError(null);
    if (!form.title.trim()) {
      setError(t('consensus.assumptionValidation.titleRequired'));
      return;
    }
    if (!form.start_bucket || !form.end_bucket) {
      setError(t('consensus.assumptionValidation.datesRequired'));
      return;
    }
    if (!form.assumption_types.length) {
      setError(t('consensus.assumptionValidation.typeRequired'));
      return;
    }
    const payload = await buildPayload();
    if (!payload) {
      setError(t('consensus.assumptionValidation.impactsRequired'));
      return;
    }
    try {
      if (editingId != null) {
        await onUpdate(editingId, payload);
      } else {
        await onCreate(payload);
      }
      closeForm();
    } catch {
      setError(t('consensus.assumptionValidation.saveFailed'));
    }
  };

  return (
    <div className="mt-4">
      {impactSummary ? (
        <div className="mb-4 rounded-lg border border-gray-200 bg-gradient-to-r from-slate-50 to-white px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {t('consensus.impactSummaryTitle')}
              </p>
              <p className="mt-0.5 text-xs text-gray-500">
                {t('consensus.impactSummaryScope', {
                  count: impactSummary.assumption_count,
                })}
              </p>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <div>
                <div className="text-xs text-gray-500">
                  {t('consensus.impactSummaryUnits')}
                </div>
                <div className={`font-semibold ${variationClass(impactSummary.impact_qty)}`}>
                  {formatSignedNumber(impactSummary.impact_qty)}
                  {impactSummary.impact_pct != null ? (
                    <span className="ml-1 text-xs font-normal text-gray-500">
                      ({formatVariation(impactSummary.impact_pct)})
                    </span>
                  ) : null}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">
                  {t('consensus.impactSummaryValue')}
                </div>
                <div className={`font-semibold ${variationClass(impactSummary.impact_value)}`}>
                  {formatSignedEuro(impactSummary.impact_value)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">
                  {t('consensus.impactSummaryBase')}
                </div>
                <div className="font-semibold text-gray-900">
                  {formatNumber(impactSummary.base_forecast_qty)}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">{t('consensus.sopAssumptionsTitle')}</h3>
          <p className="text-xs text-gray-500">{cycleTitle}</p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md bg-stocker-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          onClick={() => (showForm && editingId == null ? closeForm() : (resetForm(), setShowForm(true)))}
        >
          <Plus className="h-4 w-4" />
          {t('consensus.newAssumption')}
        </button>
      </div>

      {showForm ? (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h4 className="mb-3 text-xs font-semibold uppercase text-gray-600">
            {editingId != null ? t('consensus.editAssumption') : t('consensus.newAssumption')}
          </h4>
          {loadingEdit ? (
            <p className="text-sm text-gray-500">{t('common.loading')}</p>
          ) : (
            <div className={loadingEdit ? 'pointer-events-none opacity-50' : ''}>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-xs text-gray-600 md:col-span-2">
                  {t('consensus.assumptionText')}
                  <input
                    className={inputClass}
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  />
                </label>
                <label className="text-xs text-gray-600 md:col-span-2">
                  {t('consensus.comments')}
                  <input
                    className={inputClass}
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </label>
                <label className="text-xs text-gray-600">
                  {t('consensus.effectiveIn')}
                  <input
                    type="date"
                    className={inputClass}
                    value={form.start_bucket}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => ({ ...f, start_bucket: v }));
                      setImpactRows((rows) =>
                        rows.map((r) => ({ ...r, time_bucket: r.time_bucket || v })),
                      );
                    }}
                  />
                </label>
                <label className="text-xs text-gray-600">
                  {t('consensus.effectiveOut')}
                  <input
                    type="date"
                    className={inputClass}
                    value={form.end_bucket}
                    onChange={(e) => setForm((f) => ({ ...f, end_bucket: e.target.value }))}
                  />
                </label>
                <label className="text-xs text-gray-600">
                  {t('assumptions.severity')}
                  <select
                    className={inputClass}
                    value={form.severity}
                    onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
                  >
                    <option value="low">low</option>
                    <option value="medium">medium</option>
                    <option value="high">high</option>
                    <option value="critical">critical</option>
                  </select>
                </label>
                <label className="text-xs text-gray-600">
                  {t('assumptions.probability')}
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className={inputClass}
                    value={form.probabilityPercent}
                    onChange={(e) => setForm((f) => ({ ...f, probabilityPercent: e.target.value }))}
                  />
                </label>
                <div className="md:col-span-2">
                  <span className="text-xs font-medium text-gray-600">
                    {t('assumptions.assumptionType')}
                  </span>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {ASSUMPTION_TYPES.map((type) => (
                      <label key={type} className="inline-flex items-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          checked={form.assumption_types.includes(type)}
                          onChange={() => {
                            setForm((f) => {
                              const has = f.assumption_types.includes(type);
                              const next = has
                                ? f.assumption_types.filter((x) => x !== type)
                                : [...f.assumption_types, type];
                              return { ...f, assumption_types: next.length ? next : [type] };
                            });
                          }}
                        />
                        {t(`consensus.assumptionTypes.${type}`, type)}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                  {impactRows.map((row, index) => (
                    <div
                      key={index}
                      className="grid gap-3 rounded-md border border-gray-200 bg-white p-3 md:grid-cols-2"
                    >
                      <div className="md:col-span-2">
                        <EntityScopeFilterBar
                          {...sopEntityScopeFilterBarOptions(scope)}
                          disabledFilterIds={[]}
                          value={{
                            family_codes: row.family_codes,
                            subfamily_codes: row.subfamily_codes,
                            warehouse_codes: [],
                            supplier_codes: row.supplier_codes,
                            product_codes: row.product_codes,
                          }}
                          onChange={(next: EntityScopeFilters) =>
                            setImpactRows((rows) =>
                              rows.map((r, i) =>
                                i === index
                                  ? {
                                      ...r,
                                      family_codes: next.family_codes,
                                      subfamily_codes: next.subfamily_codes,
                                      warehouse_codes: [],
                                      supplier_codes: next.supplier_codes,
                                      product_codes: next.product_codes,
                                    }
                                  : r,
                              ),
                            )
                          }
                        />
                      </div>
                      <label className="text-xs text-gray-600">
                        {t('consensus.impactType')}
                        <select
                          className={inputClass}
                          value={row.impact_type}
                          onChange={(e) =>
                            setImpactRows((rows) =>
                              rows.map((r, i) =>
                                i === index
                                  ? {
                                      ...r,
                                      impact_type: e.target.value as 'percentage' | 'absolute_qty',
                                    }
                                  : r,
                              ),
                            )
                          }
                        >
                          <option value="percentage">{t('consensus.impactTypePercentage')}</option>
                          <option value="absolute_qty">{t('consensus.impactTypeAbsolute')}</option>
                        </select>
                      </label>
                      {row.impact_type === 'percentage' ? (
                        <label className="text-xs text-gray-600">
                          {t('consensus.impactPercentage')}
                          <input
                            type="number"
                            className={inputClass}
                            value={row.impact_percentage}
                            onChange={(e) =>
                              setImpactRows((rows) =>
                                rows.map((r, i) =>
                                  i === index ? { ...r, impact_percentage: e.target.value } : r,
                                ),
                              )
                            }
                          />
                        </label>
                      ) : (
                        <label className="text-xs text-gray-600">
                          {t('consensus.impactQty')}
                          <input
                            type="number"
                            className={inputClass}
                            value={row.impact_qty}
                            onChange={(e) =>
                              setImpactRows((rows) =>
                                rows.map((r, i) =>
                                  i === index ? { ...r, impact_qty: e.target.value } : r,
                                ),
                              )
                            }
                          />
                        </label>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="text-xs text-stocker-primary hover:underline"
                    onClick={() =>
                      setImpactRows((rows) => [...rows, emptyImpactRow(form.start_bucket)])
                    }
                  >
                    {t('consensus.addImpactRow')}
                  </button>
                </div>

              {error ? (
                <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </p>
              ) : null}

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  className="rounded-md bg-stocker-primary px-4 py-2 text-sm text-white disabled:opacity-50"
                  disabled={saving}
                  onClick={() => void submit()}
                >
                  {saving ? t('common.saving') : t('common.save')}
                </button>
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm"
                  onClick={closeForm}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">{t('consensus.assumptionText')}</th>
              <th className="px-3 py-2">{t('consensus.effectivePeriod')}</th>
              <th className="px-3 py-2 text-right">
                {t('consensus.impactUnits')}
              </th>
              <th className="px-3 py-2 text-right">{t('consensus.impactPercent')}</th>
              <th className="px-3 py-2 text-right">
                {t('consensus.impactValue', { currencySymbol: symbol })}
              </th>
              <th className="px-3 py-2 text-right">
                {t('assumptions.probability')}
              </th>
              <th className="px-3 py-2">{t('assumptions.severity')}</th>
              <th className="px-3 py-2 w-24" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                  {t('common.loading')}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                  {t('consensus.noAssumptions')}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => (
                <tr key={row.id} className="border-t border-gray-100">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{row.title}</div>
                    <div className="text-xs text-gray-500">{row.assumption_type}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-600">
                    {formatDate(row.start_bucket)} – {formatDate(row.end_bucket)}
                  </td>
                  <td
                    className={`whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums ${variationClass(row.impact_qty)}`}
                  >
                    {formatSignedNumber(row.impact_qty)}
                  </td>
                  <td
                    className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${variationClass(row.impact_pct)}`}
                  >
                    {formatVariation(row.impact_pct)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700">
                    {formatSignedEuro(row.impact_value)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700">
                    {formatProbability(row.probability)}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                        SEVERITY_STYLES[row.severity] ?? 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {t(`assumptions.severityLevels.${row.severity}`, row.severity)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        className="rounded p-1 text-gray-500 hover:bg-gray-100"
                        title={t('common.edit')}
                        onClick={() => void startEdit(row)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="rounded p-1 text-red-600 hover:bg-red-50"
                        disabled={deleting}
                        title={t('common.delete')}
                        onClick={() => setDeleteTarget(row)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <SopConfirmDialog
        open={deleteTarget != null}
        title={t('consensus.deleteAssumptionTitle')}
        message={t('consensus.confirmDeleteAssumption', { title: deleteTarget?.title ?? '' })}
        confirmLabel={t('common.delete')}
        variant="destructive"
        busy={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
